import { FinancialAccount } from "@common/types/financialAccount";
import { IntegrationAccount } from "@common/types/integrationAccount";
import { parseISO, subMinutes } from "date-fns";
import { timeAgoInSentence } from "./date";

type SyncTimestampFields = Pick<
    IntegrationAccount,
    "balanceSyncedAt" | "transactionsSyncedAt"
>;

export type AccountSyncTimesSource = Pick<
    FinancialAccount,
    "balanceSyncedAt" | "transactionsSyncedAt"
> & {
    integrationAccounts?: SyncTimestampFields[];
};

export interface AccountSyncTimes {
    transactionsSyncedAt: Date | null;
    balanceSyncedAt: Date | null;
    lastSyncedAt: Date | null;
}

export function getDemoSyncedAt(): Date {
    return subMinutes(new Date(), 4);
}

export function getLatestDate(
    dates: Array<Date | string | null | undefined>,
): Date | null {
    return dates.reduce<Date | null>((latest, value) => {
        if (!value) {
            return latest;
        }
        const date = typeof value === "string" ? parseISO(value) : value;
        return !latest || date.getTime() > latest.getTime() ? date : latest;
    }, null);
}

export function getAccountSyncTimes(
    account: AccountSyncTimesSource,
): AccountSyncTimes {
    const sources: SyncTimestampFields[] = [
        account,
        ...(account.integrationAccounts ?? []),
    ];
    const transactionsSyncedAt = getLatestDate(
        sources.map((source) => source.transactionsSyncedAt),
    );
    const balanceSyncedAt = getLatestDate(
        sources.map((source) => source.balanceSyncedAt),
    );

    return {
        transactionsSyncedAt,
        balanceSyncedAt,
        lastSyncedAt: getLatestDate([transactionsSyncedAt, balanceSyncedAt]),
    };
}

export function getSyncBreakdownBullets({
    transactionsSyncedAt,
    balanceSyncedAt,
}: AccountSyncTimes): string[] {
    const bullets: string[] = [];
    if (transactionsSyncedAt) {
        bullets.push(
            `Transactions: synced ${timeAgoInSentence(transactionsSyncedAt)}`,
        );
    }
    if (balanceSyncedAt) {
        bullets.push(`Balance: updated ${timeAgoInSentence(balanceSyncedAt)}`);
    }
    return bullets;
}
